import { Button } from "@/components/ui/button";
import { FieldError, Field, FieldLabel } from "@/components/ui/field";
import { Heading } from "@/components/heading";
import { Input } from "@/components/ui/input";
import { Text, TextLink } from "@/components/text";
import {
	createFileRoute,
	redirect,
	useLoaderData,
	useNavigate,
	useRouteContext,
	useRouter,
	useSearch,
} from "@tanstack/react-router";
import { useForm } from "@tanstack/react-form";
import { z } from "zod";
import { Spinner } from "@/components/ui/spinner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useServerFn } from "@tanstack/react-start";
import { createAccount as createAccountHandler } from "@/handlers/createAccount";
import { Icon } from "@/components/icon";
import { AnalyticsEvents, useAnalytics } from "@/utils/analytics";
import axios from "axios";
import { toast } from "sonner";
import { commitLocalUpdate, fetchQuery, graphql } from "relay-runtime";
import { environment } from "@/relay/environment";
import type { cadastro_inviteQuery } from "./__generated__/cadastro_inviteQuery.graphql";
import { useEffect, useState } from "react";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupInput,
} from "@/components/ui/input-group";

export const Route = createFileRoute("/_auth/(no-auth)/cadastro")({
	component: RouteComponent,
	validateSearch: z.object({
		email: z.string().email().optional(),
		code: z.string().optional(),
	}),
	loaderDeps: ({ search }) => [search.email, search.code, search.redirectTo],
	loader: async ({ deps, context: { club } }) => {
		const code = deps[1];

		if (club?.visibility === "PRIVATE" && !deps[0] && !deps[1]) {
			throw redirect({
				to: "/login",
			});
		}

		if (code) {
			const data = await fetchQuery<cadastro_inviteQuery>(
				environment,
				graphql`
  		    query cadastro_inviteQuery($code: String!) {
  					invite(code: $code) {
              email
              code
  		        club {
  						  id
  					  }
              status
              user {
                role
              }
  					}
  				}
  			`,
				{ code },
			).toPromise();

			const isInviteValid =
				data?.invite?.club?.id === club?.id &&
				data?.invite?.status === "PENDING";

			if (!isInviteValid) {
				return { invite: undefined };
			}

			if (data?.invite?.user?.role === "authenticated") {
				throw redirect({
					to: "/login",
					search: { redirectTo: deps[2], code: deps[1] },
				});
			}

			if (
				data?.invite?.club?.id === club?.id &&
				data?.invite?.status === "PENDING"
			) {
				return { invite: data?.invite };
			}
		}

		return { invite: undefined };
	},
});

function RouteComponent() {
	const { invite } = useLoaderData({ from: Route.id });
	const { club } = useRouteContext({ from: "__root__" });
	const { email, code, redirectTo } = useSearch({ from: Route.id });
	const navigate = useNavigate({ from: Route.fullPath });
	const router = useRouter();

	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	const { track } = useAnalytics();
	const createAccount = useServerFn(createAccountHandler);
	const form = useForm({
		defaultValues: {
			email: invite?.email || email || "",
			username: "",
			password: "",
			confirmPassword: "",
		},
		validators: {
			onChangeAsyncDebounceMs: 600,
			onChangeAsync: z
				.object({
					username: z
						.string()
						.min(2, "Nome de usuário precisa ter no mínimo 2 caracteres")
						.regex(
							/^[a-zA-Z0-9_-]+$/,
							"Nome de usuário deve conter apenas letras, números, underline e hífen (sem espaços)",
						),
					email: z.string().email("Email inválido"),
					password: z.string().min(1, "A senha é obrigatória"),
					confirmPassword: z.string().min(1, "Confirme sua senha"),
				})
				.superRefine(async (data, ctx) => {
					if (data.password.length < 8) {
						ctx.addIssue({
							code: "custom",
							message: "A senha precisa ter no mínimo 8 caracteres",
							path: ["password"],
							input: data.password,
						});
					}

					// validate if password contains at least one uppercase letter
					if (!/[A-Z]/.test(data.password)) {
						ctx.addIssue({
							code: "custom",
							message: "A senha precisa conter pelo menos uma letra maiúscula",
							path: ["password"],
							input: data.password,
						});
					}

					// validate if password contains at least one lowercase letter
					if (!/[a-z]/.test(data.password)) {
						ctx.addIssue({
							code: "custom",
							message: "A senha precisa conter pelo menos uma letra minúscula",
							path: ["password"],
							input: data.password,
						});
					}

					// validate if password contains at least one number
					if (!/\d/.test(data.password)) {
						ctx.addIssue({
							code: "custom",
							message: "A senha precisa conter pelo menos um número",
							path: ["password"],
							input: data.password,
						});
					}

					// validate if password contains at least one special character
					if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(data.password)) {
						ctx.addIssue({
							code: "custom",
							message:
								"A senha precisa conter pelo menos um caractere especial",
							path: ["password"],
							input: data.password,
						});
					}

					if (data.password !== data.confirmPassword) {
						ctx.addIssue({
							code: "custom",
							message: "As senhas não coincidem",
							path: ["confirmPassword"],
							input: data.password,
						});
					}

					const isUsernameValid = await axios
						.head(`${import.meta.env.VITE_SERVER_URL}/u/${data.username}`)
						.then(() => false)
						.catch(() => true);

					if (!isUsernameValid) {
						ctx.addIssue({
							code: "custom",
							message: "Esse nome de usuário já está em uso",
							path: ["username"],
							input: data.username,
						});
					}
				}),
		},
		onSubmit: async ({ value: { confirmPassword, ...value } }) => {
			return await createAccount({ data: { ...value, code: invite?.code } })
				.then(async (data) => {
					track(AnalyticsEvents.AUTH_SIGNUP_COMPLETED, {
						has_invite: !!invite?.code,
					});

					commitLocalUpdate(environment, (store) => store.invalidateStore());

					await router.invalidate();

					if (data) {
						navigate({
							to: redirectTo || "/",
						});
					}
				})
				.catch((err) => {
					track(AnalyticsEvents.AUTH_SIGNUP_FAILED, {
						error: err.message || "unknown",
					});

					if (err.message?.includes("already exists")) {
						toast.error("Já existe uma conta com esse email");
					} else {
						toast.error("Não foi possível criar uma conta no momento");
					}

					throw err;
				});
		},
	});

	useEffect(() => {
		if (!invite && code && !form.state.isSubmitted) {
			const timeout = setTimeout(() => {
				toast.error("Esse convite já foi utilizado.");
			}, 300);

			return () => {
				clearTimeout(timeout);
			};
		}
	}, [invite, code, form.state.isSubmitted]);

	return (
		<div className="flex justify-center w-full">
			<div className="flex w-full flex-col gap-8 justify-center p-6 max-w-[550px]">
				<div className="size-12">
					<Avatar>
						<AvatarFallback>
							{club?.name.slice(0, 2).toUpperCase()}
						</AvatarFallback>
						<AvatarImage src={club?.logo?.signedUrl} />
					</Avatar>
				</div>

				<Heading>Crie sua conta!</Heading>

				<form
					className="flex flex-col gap-8"
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
				>
					<form.Field name="username">
						{(field) => (
							<Field>
								<FieldLabel htmlFor={field.name}>Nome de Usuário</FieldLabel>
								<InputGroup>
									<InputGroupAddon>
										<Icon name="AtSign" className="size-4" />
									</InputGroupAddon>

									<InputGroupInput
										id={field.name}
										name={field.name}
										value={field.state.value}
										onBlur={field.handleBlur}
										onChange={(e) => field.handleChange(e.target.value)}
										placeholder="nome_de_usuario"
										aria-invalid={
											!!(
												field.state.meta.isTouched && field.state.meta.errors[0]
											)
										}
									/>
								</InputGroup>

								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</Field>
						)}
					</form.Field>

					<form.Field name="email">
						{(field) => (
							<Field>
								<FieldLabel htmlFor={field.name}>Email</FieldLabel>
								<Input
									id={field.name}
									name={field.name}
									value={field.state.value}
									onBlur={field.handleBlur}
									placeholder="Email"
									disabled={!!(invite?.email || email)}
									onChange={(e) => field.handleChange(e.target.value)}
									type="email"
									aria-invalid={
										!!(field.state.meta.isTouched && field.state.meta.errors[0])
									}
								/>

								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</Field>
						)}
					</form.Field>

					<form.Field name="password">
						{(field) => (
							<Field>
								<FieldLabel htmlFor={field.name}>Senha</FieldLabel>

								<InputGroup>
									<InputGroupInput
										id={field.name}
										name={field.name}
										value={field.state.value}
										onBlur={field.handleBlur}
										onChange={(e) => field.handleChange(e.target.value)}
										placeholder="Senha"
										type={showPassword ? "text" : "password"}
										autoCapitalize="off"
										autoCorrect="off"
										autoComplete="new-password"
										aria-invalid={
											!!(
												field.state.meta.isTouched && field.state.meta.errors[0]
											)
										}
									/>

									<InputGroupAddon align="inline-end">
										<InputGroupButton
											type="button"
											onClick={() => setShowPassword((p) => !p)}
											tabIndex={-1}
										>
											<Icon name={showPassword ? "EyeOff" : "Eye"} />
										</InputGroupButton>
									</InputGroupAddon>
								</InputGroup>

								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</Field>
						)}
					</form.Field>

					<form.Field name="confirmPassword">
						{(field) => (
							<Field>
								<FieldLabel htmlFor={field.name}>Confirme sua Senha</FieldLabel>
								<InputGroup>
									<InputGroupInput
										id={field.name}
										name={field.name}
										value={field.state.value}
										onBlur={field.handleBlur}
										onChange={(e) => field.handleChange(e.target.value)}
										placeholder="Confirme sua Senha"
										type={showConfirmPassword ? "text" : "password"}
										autoCapitalize="off"
										autoCorrect="off"
										autoComplete="new-password"
										aria-invalid={
											!!(
												field.state.meta.isTouched && field.state.meta.errors[0]
											)
										}
									/>

									<InputGroupAddon align="inline-end">
										<InputGroupButton
											onClick={() => setShowConfirmPassword((p) => !p)}
										>
											<Icon name="Eye" />
										</InputGroupButton>
									</InputGroupAddon>
								</InputGroup>

								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</Field>
						)}
					</form.Field>

					<Text className="text-sm text-muted-foreground">
						Ao criar a conta estou de acordo com os{" "}
						<TextLink href="https://www.scarf.club/termos-de-uso/">
							Termos de uso
						</TextLink>{" "}
						e{" "}
						<TextLink href="https://www.scarf.club/politica-de-privacidade/">
							Política de Privacidade
						</TextLink>
					</Text>

					<form.Subscribe
						selector={(state) => [state.isSubmitting, state.isSubmitSuccessful]}
					>
						{([isSubmitting, isSubmitSuccessful]) => (
							<div className="flex flex-col gap-6">
								<Button
									type="submit"
									color={isSubmitSuccessful ? "emerald" : "primary"}
									disabled={isSubmitting || isSubmitSuccessful}
									className="flex items-center justify-center gap-2"
								>
									{isSubmitSuccessful ? (
										<Icon name="CircleCheck" className="size-4" />
									) : null}

									{!isSubmitSuccessful && (
										<>Criar Conta {isSubmitting ? <Spinner /> : null}</>
									)}
								</Button>

								<Text className="font-medium">
									Já tem uma conta? <TextLink to="/login">Faça Login</TextLink>
								</Text>
							</div>
						)}
					</form.Subscribe>
				</form>
			</div>
		</div>
	);
}
